import React from 'react';
import { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { freshsalesService, type FreshsalesCallData } from '@/services/freshsales';

export interface FreshsalesOutboundCallEvent {
  phoneNumber: string;
}

interface FreshsalesContextType {
  isInitialized: boolean;
  currentOutboundEvent: FreshsalesOutboundCallEvent | null;
  currentCall: FreshsalesCallData | null;
  setCurrentOutboundEvent: (event: FreshsalesOutboundCallEvent | null) => void;
  setIsInitialized: (initialized: boolean) => void;
  startOutboundCall: (phoneNumber: string, contactId?: string) => Promise<string>;
  endCall: (status?: FreshsalesCallData['status']) => Promise<void>;
  updateCallNotes: (notes: string) => Promise<void>;
}

const FreshsalesContext = createContext<FreshsalesContextType | null>(null);

interface FreshsalesProviderProps {
  children: React.ReactNode;
}

export const FreshsalesProvider: React.FC<FreshsalesProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentOutboundEvent, setCurrentOutboundEvent] =
    useState<FreshsalesOutboundCallEvent | null>(null);
  const [currentCall, setCurrentCall] = useState<FreshsalesCallData | null>(null);

  const handleSetIsInitialized = useCallback((initialized: boolean) => {
    console.log('[Freshsales Provider] Setting initialized state:', initialized);
    setIsInitialized(initialized);
  }, []);

  const handleSetCurrentOutboundEvent = useCallback((event: FreshsalesOutboundCallEvent | null) => {
    console.log('[Freshsales Provider] Setting current outbound event:', event);
    setCurrentOutboundEvent(event);
  }, []);

  const startOutboundCall = useCallback(async (phoneNumber: string, contactId?: string) => {
    console.log('[Freshsales Provider] Starting outbound call for:', phoneNumber);
    const externalCallId = await freshsalesService.startOutboundCall(phoneNumber, contactId);
    setCurrentCall(freshsalesService.getCurrentCall());
    return externalCallId;
  }, []);

  const endCall = useCallback(
    async (status?: FreshsalesCallData['status']) => {
      console.log('[Freshsales Provider] Ending call:', currentCall?.externalCallId);
      await freshsalesService.endCall(status);
      setCurrentCall(null);
    },
    [currentCall],
  );

  const updateCallNotes = useCallback(
    async (notes: string) => {
      console.log('[Freshsales Provider] Updating call notes:', currentCall?.externalCallId);
      await freshsalesService.updateCallNotes(notes);
      setCurrentCall(freshsalesService.getCurrentCall());
    },
    [currentCall],
  );

  useEffect(() => {
    if (isInitialized) {
      freshsalesService.initialize();
    }
  }, [isInitialized]);

  const contextValue: FreshsalesContextType = {
    isInitialized,
    currentOutboundEvent,
    currentCall,
    setCurrentOutboundEvent: handleSetCurrentOutboundEvent,
    setIsInitialized: handleSetIsInitialized,
    startOutboundCall,
    endCall,
    updateCallNotes,
  };

  return <FreshsalesContext.Provider value={contextValue}>{children}</FreshsalesContext.Provider>;
};

export const useFreshsales = (): FreshsalesContextType => {
  const context = useContext(FreshsalesContext);
  if (!context) {
    throw new Error('useFreshsales must be used within a FreshsalesProvider');
  }
  return context;
};
